import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  buildRole17FlatKey,
  getRole17Schema,
  getRole17ValidatingControls,
  isRole17CalculatedControl,
  isRole17Empty,
  isRole17StaticallyRequired,
  resolveRole17ColumnSuffixes,
  resolveRole17LayoutFlags,
  Role17Schema,
} from './role17-cash-flows-direct.rules';

/**
 * Pure validator for Role 17 (Statement of Cash Flows — Direct Method).
 *
 * Mirrors the editor's
 * role17-510000-statement-of-cash-flows-direct.component.ts sheet-status
 * surface. The component does NOT call
 * `stateService.updateSheetLevelStatus('statementOfCashFlowsDirectMethod',
 * ...)` anywhere: the only sheet-status write is
 * `markSheetInitializedWithStatus('statementOfCashFlowsDirectMethod', false)`
 * in `loadSchema()` after a 600 ms settle timeout. There is no
 * `evaluateSheetStatus()` loop, no `Validators.required` is wired in
 * `buildForm()` (empty `validators: ValidatorFn[] = []`), and the schema
 * declares `required: false` implicitly on every control (zero `required:
 * true`, zero `validators` arrays, zero `validation.equality` blocks).
 *
 * Net effect: the editor's published `hasErrors` for
 * `statementOfCashFlowsDirectMethod` is deterministically `false` today.
 * The pure validator returns `hasErrors: false` deterministically but
 * carries the LB-001 fix-guard pattern so any future schema edit that adds
 * a required cell is picked up automatically — matching the guards carried
 * by roles 22 / 21 / 13 / 9 / 14 / 11 (LB-009 / LB-010 / LB-011 / LB-012 /
 * LB-013 / LB-016 / LB-017).
 *
 * ---- Calculations --------------------------------------------------------
 *
 * Role 17's component performs six row-level calculations per visible column
 * suffix (see `ROLE17_CALCULATION_GROUPS` in the rules file):
 *
 *   1. CashGeneratedFromOperations
 *   2. NetCashFromOperatingActivities
 *   3. NetCashUsedInInvestingActivities
 *   4. NetCashFromFinancingActivities
 *   5. NetIncreaseDecreaseInCashAndCashEquivalents
 *   6. CashAndCashEquivalentsAtEndOfYear
 *
 * Per the Tier 4 Batch 1 parity stance (read persisted post-calc values,
 * don't replay arithmetic), the validator does NOT replay these formulas.
 * Calculated cells are persisted back into the sheet's payload via
 * `form.get(...)?.setValue(..., { emitEvent: false })`, so the validator
 * reads post-calculation values directly from `sheetData`. Since no
 * equality block and no required cell consume the calculated results, the
 * calculations do not influence the validation outcome today. The row-group
 * table is exported so a future equality block plugs in without re-deriving
 * the column expansion.
 *
 * ---- T-010 sweep (LB-015 parallel) --------------------------------------
 *
 * **Checked explicitly.** Both `calculateTotals()` and
 * `registerCalculatedKeys()` reference `ssmt:`-prefixed qnames
 * (e.g. `ssmt:CashReceiptsFromCustomers`,
 * `ssmt:CashGeneratedFromOperations`); the schema ships the same
 * `ssmt:`-prefixed qnames. Every qname referenced by the arithmetic DOES
 * exist in the schema — no LB-015 divergence. Unlike role 14 (where
 * `ssmt:` references pointed at `ifrs-full:` / `ifrs-smes:` qnames that
 * don't exist in the schema), role 17's direct-method layout uses only
 * `ssmt:` qnames on both sides of the bridge. The rules file's
 * `ROLE17_CALCULATION_GROUPS` is the single source of truth so a future
 * prefix rename fails loudly at the calculation call site rather than
 * silently returning zeros.
 *
 * ---- Cross-sheet posture (Tier 4) ---------------------------------------
 *
 * Role 17 is classified Tier 4 because of its row-level calculations.
 * Per `SheetDependencyService`:
 *   • `scopeOfFiling` is an UPSTREAM prerequisite (column-visibility
 *     driver: consolidation + comparative-year toggles). Submitting
 *     `scopeOfFiling` invalidates `statementOfCashFlowsDirectMethod`'s
 *     cached status.
 *   • `filingPrerequisites` (Role 0) is the central config hub —
 *     submitting it invalidates all sheets, including role 17.
 *   • Role 17 is NOT a key in the dependency map — no downstream sheet
 *     reads from role 17 (role 17 is an alternative presentation to role
 *     18, not a feeder). This asymmetry reflects role 17's schema
 *     declaring ZERO `validation.equality` blocks: nothing outside role
 *     17 compares against it.
 *
 * The component's `setupLayoutReactivity()` watches
 * `slicesOfInterest: ['filingPrerequisites', 'scopeOfFiling']` and the
 * `modifiedState$` re-validation subscription debounces on the same two
 * slices. The validator reads `scopeOfFiling` defensively through
 * `context.crossSheetData` to drive column visibility; `filingPrerequisites`
 * is not consumed by the validator today because (a) the schema has no
 * date-gated equality rules (unlike role 8's Equity-by-year) and (b) the
 * accounting-standard branch is irrelevant — the schema is single-variant.
 *
 * Tolerated absence: when `scopeOfFiling` is missing from the bundle (parity-
 * side mid-init, or a listing/dashboard cache-miss path), the validator
 * defaults to the conservative "single Current column" layout — matching
 * the component's pre-mount initialiser values before
 * `checkConsolidatedStatus()` runs.
 */

const UPSTREAM_SCOPE_SLICE_KEY = SHEET_KEYS.scopeOfFiling;

/**
 * Mirror the component's row-level "required field with no value -> Failed"
 * branch. Structurally identical to role 11's `checkRequiredRowErrors`.
 * Today this loop is a no-op against the live schema (zero required
 * controls) but the LB-001 fix-guard ensures any future schema edit gets
 * picked up automatically.
 *
 * Calculated / readonly rows are skipped — their values are produced by
 * the component's `calculateTotals()` after valueChanges settle. If a future
 * schema edit ever sets `required: true` on a calculated row, the skip
 * preserves the behaviour the component exhibits (the row would still
 * receive a value via setValue, so flagging it as "empty" would be
 * misleading).
 */
function checkRequiredRowErrors(
  schema: Role17Schema,
  sheetData: Record<string, any>,
  context: SheetValidationContext
): Record<string, string> {
  const errors: Record<string, string> = {};

  const scopeSlice = context.crossSheetData?.get(UPSTREAM_SCOPE_SLICE_KEY);
  const { isConsolidated, hasPreviousYear } =
    resolveRole17LayoutFlags(scopeSlice);
  const suffixes = resolveRole17ColumnSuffixes(
    isConsolidated,
    hasPreviousYear
  );

  for (const control of getRole17ValidatingControls(schema)) {
    if (!isRole17StaticallyRequired(control)) continue;
    if (isRole17CalculatedControl(control)) continue;

    // Every validating control on this sheet is dual-column (every
    // non-header declares `ui.dualColumn: true`). Mirror
    // getRowValidationStatus(): a row is "Failed" only when ALL visible
    // columns are empty — if any column has a value, the row is
    // considered satisfied. (Role 11 uses the same per-row policy.)
    const keys = suffixes.map((suffix) => buildRole17FlatKey(control.qname, suffix));
    const anyFilled = keys.some((key) => !isRole17Empty(sheetData[key]));
    if (!anyFilled) {
      errors[control.qname] = `${control.qname} is required`;
    }
  }

  return errors;
}

export const role17CashFlowsDirectValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.statementOfCashFlowsDirectMethod,
  // Tier 4 per phase-2 plan: role 17 performs six row-level calculations
  // per visible column suffix, matching the Tier 4 calculation-parity
  // profile (roles 8 / 10 / 11 / 12 / 14). The validator's output is
  // deterministic on sheetData + scopeOfFiling alone today (no equality
  // blocks, no required cells), but the Tier 4 classification stays so
  // the parity harness exercises both paths and a future schema edit
  // that adds a genuine equality check plugs in without re-tiering.
  tier: 4,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const schema = getRole17Schema();

    const errors: Record<string, string> = {
      ...checkRequiredRowErrors(schema, sheetData, context),
    };

    return {
      // Structurally `false` today — the schema declares no required cells,
      // no equality rules, no validators. The spread above is kept so the
      // LB-001 fix-guard in the rules file starts flagging the day a
      // schema edit adds a required cell. Parity-wise this still matches
      // the editor's deterministic `false` because `isRole17StaticallyRequired()`
      // returns false on every control today.
      hasErrors: Object.keys(errors).length > 0,
      // Developer Note (TODO): stub — `hasSuggestedChanges` is hard-coded false,
      // so this sheet's dropdown "*" can never light. When released as a live
      // monetary sheet, mirror role13: (1) recompute via
      // CalculationService.calculateTotals_<id> + hasCalculatedFieldMismatch(
      // context.extractedBaseline ?? sheetData, recomputed); (2) add its sheet key
      // to PURE_VERDICT_SHEETS; (3) call evaluateSheetStatus() in the component's
      // runAllValidations(); (4) add its sheet key to CALC_DIFF_SHEETS in
      // suggestion-capability.ts, or the "(*) Optional: Suggestions to refine
      // data" legend will stay hidden on this sheet even once its "*" can light.
      hasSuggestedChanges: false,
      errors,
    };
  },
};
